/**
 * Job Detail Modal Component
 *
 * Shows full job details with timeline and admin actions
 * Uses real-time InstantDB subscription for live progress updates
 */
import { useState } from "react";
import { useSnapshot } from "valtio";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  RotateCcw,
  Ban,
  Copy,
  Loader2,
  Trash2,
  RefreshCw,
} from "lucide-react";
import { JobStagesPipeline } from "@/features/admin/components/JobStagesPipeline";
import { toast } from "sonner";
import { JobStatusBadge } from "@/features/admin/components/JobStatusBadge";
import {
  jobsStore,
  retryJob,
  cancelJob,
  deleteJob,
} from "@/features/admin/stores/jobs";
import { useProcessingJobById, type JobStatus } from "@/lib/instantdb";

// Type for job with project relation from InstantDB
interface JobWithProject {
  id: string;
  projectId: string;
  status: string;
  progress: number;
  message: string;
  startedAt: number;
  completedAt?: number;
  outputR2Key?: string;
  error?: string;
  analyticsJson?: Record<string, unknown>;
  edlJson?: Record<string, unknown>;
  // External job tracking (for async webhook-based rendering)
  externalJobId?: string;
  externalProvider?: string;
  project?: Array<{
    id: string;
    userId: string;
    name: string;
    style: string;
    duration: number;
  }>;
}

interface JobDetailModalProps {
  job: JobWithProject;
  onClose: () => void;
}

export function JobDetailModal({ job: initialJob, onClose }: JobDetailModalProps) {
  const { actionLoading } = useSnapshot(jobsStore);

  // Subscribe to real-time updates for this specific job
  const { data } = useProcessingJobById(initialJob.id);

  // Use real-time data if available, fall back to initial prop
  const liveJob = data?.processing_jobs?.[0];
  const job: JobWithProject = liveJob
    ? {
        id: liveJob.id,
        projectId: liveJob.projectId as string,
        status: liveJob.status as string,
        progress: liveJob.progress as number,
        message: liveJob.message as string,
        startedAt: liveJob.startedAt as number,
        completedAt: liveJob.completedAt as number | undefined,
        outputR2Key: liveJob.outputR2Key as string | undefined,
        error: liveJob.error as string | undefined,
        analyticsJson: liveJob.analyticsJson as Record<string, unknown> | undefined,
        edlJson: liveJob.edlJson as Record<string, unknown> | undefined,
        externalJobId: liveJob.externalJobId as string | undefined,
        externalProvider: liveJob.externalProvider as string | undefined,
        project: (liveJob.project as JobWithProject["project"]) || initialJob.project,
      }
    : initialJob;

  const project = job.project?.[0];

  const canRetry = job.status === "failed";
  const canCancel = job.status === "queued";
  const canRetryWebhook = job.status === "transcoding" && job.externalJobId;
  // Allow deleting any job (stale queued/active jobs may need cleanup)
  const canDelete = true;

  const [webhookLoading, setWebhookLoading] = useState(false);

  const handleRetry = async () => {
    try {
      await retryJob(job.id);
      toast.success("Job queued for retry");
    } catch {
      toast.error("Failed to retry job");
    }
  };

  const handleCancel = async () => {
    try {
      await cancelJob(job.id);
      toast.success("Job cancelled");
    } catch {
      toast.error("Failed to cancel job");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteJob(job.id);
      toast.success("Job deleted");
      onClose();
    } catch {
      toast.error("Failed to delete job");
    }
  };

  const handleRetryWebhook = async () => {
    if (!job.externalJobId) return;

    setWebhookLoading(true);
    try {
      const response = await fetch(
        "https://vizi-webhooks.bpillow.workers.dev/webhooks/apivideo",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "video.encoding.quality.completed",
            videoId: job.externalJobId,
          }),
        }
      );

      const result = await response.json();

      if (result.success) {
        toast.success("Webhook triggered successfully");
      } else {
        toast.error(`Webhook failed: ${result.message}`);
      }
    } catch (error) {
      toast.error("Failed to trigger webhook");
      console.error("Webhook error:", error);
    } finally {
      setWebhookLoading(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  // Format date
  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleString();
  };

  // Calculate duration
  const getDuration = () => {
    if (!job.startedAt) return null;
    const end = job.completedAt ?? Date.now();
    const seconds = Math.floor((end - job.startedAt) / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] w-full max-w-2xl overflow-y-auto sm:max-w-xl md:max-w-2xl lg:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <JobStatusBadge status={job.status as JobStatus} />
            <span>Job Details</span>
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Job Stages Pipeline */}
          <JobStagesPipeline
            status={job.status as JobStatus}
            progress={job.progress}
            message={job.message}
          />

          {/* External job info (for async webhook-based rendering) */}
          {job.externalJobId && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-cyan-600">
                External Processing ({job.externalProvider || "unknown"})
              </h3>
              <div className="rounded-lg border border-cyan-200 bg-cyan-50 p-3 text-sm text-cyan-700">
                <p>
                  <strong>Provider:</strong> {job.externalProvider || "unknown"}
                </p>
                <p className="mt-1">
                  <strong>External ID:</strong>{" "}
                  <code className="rounded bg-cyan-100 px-1">
                    {job.externalJobId}
                  </code>
                </p>
                <p className="mt-2 text-xs text-cyan-600">
                  Video is being transcoded externally. A webhook will update
                  the status when complete.
                </p>
              </div>
            </div>
          )}

          {/* Error message */}
          {job.error && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-red-600">Error</h3>
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {job.error}
              </div>
            </div>
          )}

          {/* Details grid */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <DetailItem label="Job ID" value={job.id} copyable />
            <DetailItem label="Project ID" value={job.projectId} copyable />
            {project && (
              <>
                <DetailItem label="Project Name" value={project.name} />
                <DetailItem label="Style" value={project.style} />
                <DetailItem label="Duration" value={`${project.duration}s`} />
                <DetailItem label="User ID" value={project.userId} copyable />
              </>
            )}
            <DetailItem label="Started" value={formatDate(job.startedAt)} />
            {job.completedAt && (
              <DetailItem label="Completed" value={formatDate(job.completedAt)} />
            )}
            {getDuration() && (
              <DetailItem label="Duration" value={getDuration()!} />
            )}
          </div>

          {/* Output URL */}
          {job.outputR2Key && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">Output</h3>
              <div className="flex items-center gap-2 rounded-lg border bg-muted/50 p-3">
                <code className="flex-1 truncate text-sm">{job.outputR2Key}</code>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => copyToClipboard(job.outputR2Key!, "Output URL")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Admin actions */}
          <div className="flex justify-between border-t pt-4">
            <div>
              {canDelete && (
                <Button
                  variant="ghost"
                  className="text-red-600 hover:bg-red-50 hover:text-red-700"
                  onClick={handleDelete}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="mr-2 h-4 w-4" />
                  )}
                  Delete
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              {canCancel && (
                <Button
                  variant="destructive"
                  onClick={handleCancel}
                  disabled={actionLoading}
                >
                  {actionLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Ban className="mr-2 h-4 w-4" />
                  )}
                  Cancel Job
                </Button>
              )}
              {canRetryWebhook && (
                <Button
                  variant="outline"
                  onClick={handleRetryWebhook}
                  disabled={webhookLoading}
                  className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
                >
                  {webhookLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="mr-2 h-4 w-4" />
                  )}
                  Retry Webhook
                </Button>
              )}
              {canRetry && (
                <Button onClick={handleRetry} disabled={actionLoading}>
                  {actionLoading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <RotateCcw className="mr-2 h-4 w-4" />
                  )}
                  Retry Job
                </Button>
              )}
              <Button variant="outline" onClick={onClose}>
                Close
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ========== Sub-components ==========

interface DetailItemProps {
  label: string;
  value: string;
  copyable?: boolean;
}

function DetailItem({ label, value, copyable }: DetailItemProps) {
  const copyToClipboard = () => {
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  return (
    <div>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 flex items-center gap-1">
        <span className="truncate font-medium">{value}</span>
        {copyable && (
          <button
            onClick={copyToClipboard}
            className="text-muted-foreground hover:text-foreground"
          >
            <Copy className="h-3 w-3" />
          </button>
        )}
      </dd>
    </div>
  );
}
